import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { setupAssistantApi } from '../services/api';
import { speakMentorReply, unlockSpeechPlayback, cancelSpeechPlayback } from '../lib/speechPlayback';
import { VOICE_MATRIX } from '../lib/voiceProfile';
import './SetupHelpDock.css';

export const SETUP_HELP_OPEN_EVENT = 'mentology-setup-help-open';

const SETUP_HELP_TTS_MODEL = 'eleven_flash_v2_5';
const JILL_VOICE_ID = VOICE_MATRIX['English (Aust)'].Female.id;

function compactSpeech(text: string): string {
  return String(text || '').replace(/\s+/g, ' ').trim();
}

function visibleHeading(): string {
  if (typeof document === 'undefined') return '';
  const heading = document.querySelector('h1, h2, .section-header, .policy-vault-heading');
  return String(heading?.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 160);
}

const OPENING_LINES: Record<string, string> = {
  '/': 'This is the AI-Mentor landing page. Mentoring Portal is for a key you already have. New Registration starts a new account. Which one do you need?',
  '/register-select': 'Three paths: Have Demo Code, Organisation, or Individual. Which one are you?',
  '/register-private': 'This is private registration. Accept the agreement, complete payment, then copy your key into Mentoring Portal. Ready to go through it?',
  '/register-org': 'This is organisation registration. Card or invoice, then you will get an Org-ID for Mentoring Portal. Which path are you on?',
  '/register-code': 'Paste the demo setup code you were given. It is only for this form — not the portal. Do you have a PRO or ELT code?',
  '/register-mentee-code': 'This form mints your private demo portal key. Fill name and email, accept the agreement, then copy the key. Need the next step?',
  '/portal-login': 'Enter the key you were issued — not a setup code like PRO-XXX. Private keys look like PROABC or PRO-4829. Org IDs look like ELTDABC or ELT-MEN-4829. What does yours look like?',
  '/introduction': 'This is Dr. André’s handover. Let the audio finish, then use the button that appears. I will not skip it for you. What is on the button?',
  '/issued-access': 'This screen only records the Service Agreement. Tick the box, then continue to the introduction. Need a hand with that?',
  '/pro-setup': 'Let’s get your AI-Mentor ready. Preferred name, voice, business brief, and your own folder — you can connect Drive later if you want. What should we set first?',
  '/elt-setup': 'Your agreement is already signed. Next is preferred name, voice, business brief, and optionally your own folder. What would you like to do?',
  '/org-admin': 'This admin console covers contact details, AI Alignment, enrolment, human mentors, the organisation folder, and emergency contacts. Ask about any of those and I will explain it.',
  '/system-admin': 'This admin console covers contact details, enrolment, human mentors, the organisation folder, and emergency contacts. Ask about any of those and I will explain it.',
  '/mentor-console': 'This is your Human Mentor Console. I can help you find a mentee, read RED Alert, or add notes. Where should we start?',
  '/workspace': 'This is the Living Room. Type or speak. I will not give a how-to plan — I will mentor your thinking. What are you working on?',
  '/mentee-lounge': 'This is the private briefing room. Use it to review setup, then enter the Living Room. What do you want to change?',
  '/corporate-lounge': 'This is the organisation briefing room. Use Change setup if you need voice, brief, or folder. What should we update?',
  '/stress-test': 'This is a three-turn strategy stress-test. No key needed. After turn 3 it will invite you to register. Ready to begin?',
  '/guest-briefing': 'This is a three-turn strategy stress-test. No key needed. After turn 3 it will invite you to register. Ready to begin?',
  '/master-admin': 'This is Master Admin. Use the left menu for tenants, demo keys, System Data, or Maintenance Preview. Which panel?',
  '/companion-instructions': 'These are Companion onboarding notes. When you are ready, continue into the workspace. What is unclear?',
};

type ChatTurn = { role: 'user' | 'assistant'; content: string };

type BrowserSpeechRecognition = {
  lang: string;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  onresult: ((event: { results: ArrayLike<ArrayLike<{ transcript?: string }>> }) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
};

function SpeechRecognitionCtor(): (new () => BrowserSpeechRecognition) | null {
  if (typeof window === 'undefined') return null;
  const w = window as Window & {
    SpeechRecognition?: new () => BrowserSpeechRecognition;
    webkitSpeechRecognition?: new () => BrowserSpeechRecognition;
  };
  return w.SpeechRecognition || w.webkitSpeechRecognition || null;
}

export default function SetupHelpDock() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [voiceOn, setVoiceOn] = useState(true);
  const [listening, setListening] = useState(false);
  const [workspaceLocked, setWorkspaceLocked] = useState(false);
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const listRef = useRef<HTMLDivElement | null>(null);
  const recognitionRef = useRef<BrowserSpeechRecognition | null>(null);
  const greetedRef = useRef(false);

  const opening = useMemo(
    () => OPENING_LINES[pathname] || 'Ask about what you can see on this screen and I will explain it.',
    [pathname],
  );

  const speak = useCallback((text: string) => {
    if (!voiceOn) return;
    const line = compactSpeech(text);
    if (!line) return;
    void speakMentorReply(line, JILL_VOICE_ID, 'English (Aust)', {
      asOneClip: true,
      modelId: SETUP_HELP_TTS_MODEL,
    });
  }, [voiceOn]);

  const openPanel = useCallback(() => {
    unlockSpeechPlayback();
    setOpen(true);
    if (greetedRef.current) return;
    greetedRef.current = true;
    setTurns([{ role: 'assistant', content: opening }]);
    speak(opening);
  }, [opening, speak]);

  useEffect(() => {
    const onOpen = () => openPanel();
    window.addEventListener(SETUP_HELP_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(SETUP_HELP_OPEN_EVENT, onOpen);
  }, [openPanel]);

  useEffect(() => {
    setOpen(false);
    setInput('');
    setTurns([]);
    setListening(false);
    greetedRef.current = false;
    recognitionRef.current?.stop();
    cancelSpeechPlayback();
  }, [pathname]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [turns, open]);

  useEffect(() => () => {
    recognitionRef.current?.stop();
    cancelSpeechPlayback();
  }, []);

  const sendMessage = async (message: string) => {
    const trimmed = message.trim();
    if (!trimmed || busy || workspaceLocked) return;
    unlockSpeechPlayback();
    setBusy(true);
    const nextTurns: ChatTurn[] = [...turns, { role: 'user', content: trimmed }];
    setTurns(nextTurns);
    try {
      const data = await setupAssistantApi.message({
        screen: pathname,
        message: trimmed,
        heading: visibleHeading(),
        history: nextTurns.slice(0, -1).slice(-8),
      });
      const reply = String(data.reply || '').trim() || 'Let’s take the next step only. What are you looking at on this screen?';
      if (data.workspaceLocked) setWorkspaceLocked(true);
      setTurns([...nextTurns, { role: 'assistant', content: reply }]);
      if (!data.workspaceLocked) speak(reply);
    } catch {
      const fallback = 'I could not reach the setup assistant just then. Try again, or tell me which button you are looking at.';
      setTurns([...nextTurns, { role: 'assistant', content: fallback }]);
    } finally {
      setBusy(false);
    }
  };

  const send = async (event: FormEvent) => {
    event.preventDefault();
    const message = input.trim();
    if (!message) return;
    setInput('');
    await sendMessage(message);
  };

  const toggleMic = () => {
    const Ctor = SpeechRecognitionCtor();
    if (!Ctor) {
      setTurns((current) => [
        ...current,
        { role: 'assistant', content: 'Voice capture is not available in this browser. Type your question instead.' },
      ]);
      return;
    }
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const recognition = new Ctor();
    recognitionRef.current = recognition;
    recognition.lang = 'en-AU';
    recognition.interimResults = false;
    recognition.onresult = (event) => {
      const transcript = Array.from(event.results).map((result) => result[0]?.transcript || '').join(' ').trim();
      if (transcript) {
        setInput('');
        void sendMessage(transcript);
      }
    };
    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);
    setListening(true);
    recognition.start();
  };

  if (pathname === '/master-admin') return null;

  return (
    <div className="setup-assistant-root" data-setup-help="true">
      {open && (
        <section className={`setup-assistant-panel${workspaceLocked ? ' workspace-locked' : ''}`} aria-label="Setup assistant">
          <header className="setup-assistant-header">
            <h2>Setup Assistant</h2>
            <button
              type="button"
              className="setup-assistant-close"
              aria-label="Close setup assistant"
              onClick={() => {
                cancelSpeechPlayback();
                setOpen(false);
              }}
            >
              ×
            </button>
          </header>
          <p className="setup-assistant-status">
            I will speak the reply unless you mute.
          </p>
          <div className="setup-assistant-log" ref={listRef} aria-live="polite">
            {turns.map((turn, index) => (
              <p
                key={`${turn.role}-${index}`}
                className={`setup-help-bubble setup-help-bubble-${turn.role}`}
              >
                {turn.content}
              </p>
            ))}
            {busy && <p className="setup-help-bubble setup-help-bubble-assistant">One moment…</p>}
          </div>
          <form className="setup-assistant-dock" onSubmit={send}>
            <input
              className="setup-assistant-input"
              disabled={busy || workspaceLocked}
              type="text"
              maxLength={800}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={workspaceLocked ? 'Chat workspace locked' : 'Ask how to set this screen up…'}
              autoComplete="off"
              aria-label="Setup help question"
            />
            <button
              type="button"
              className={`setup-assistant-tool ${listening ? 'active' : ''}`}
              title="Voice capture"
              aria-label="Microphone"
              onClick={toggleMic}
              disabled={workspaceLocked}
            >
              Mic
            </button>
            <button
              type="button"
              className={`setup-assistant-tool ${voiceOn ? 'active' : ''}`}
              title="Read replies aloud"
              aria-pressed={voiceOn}
              onClick={() => {
                setVoiceOn((on) => {
                  if (on) cancelSpeechPlayback();
                  return !on;
                });
              }}
            >
              Voice
            </button>
            <button type="submit" className="setup-assistant-send" disabled={busy || workspaceLocked || !input.trim()}>
              Send
            </button>
          </form>
        </section>
      )}
      <button
        type="button"
        className="setup-assistant-toggle"
        aria-expanded={open}
        onClick={() => (open ? setOpen(false) : openPanel())}
      >
        Setup help
      </button>
    </div>
  );
}

export function openSetupHelp() {
  window.dispatchEvent(new Event(SETUP_HELP_OPEN_EVENT));
}
